import React from "react";

import contactAndUsEmailIcon from "../../images/andus-footer-email.svg";
import contactAndUsLinkedinIcon from "../../images/andus-footer-linkedin.svg";
import contactAndUsLocationIcon from "../../images/andus-footer-location.svg";
import contactAndUsPhoneIcon from "../../images/andus-footer-phone.svg";
import contactAndUsTpcLogo from "../../images/tpc-performance-centre-logo.png";

import "./ContactAndUsSection.css";

const TPC_WEBSITE_URL = "https://techperformancecentre.com/";

const TPC_LINKEDIN_URL =
  "https://za.linkedin.com/company/tpc-performance-centre";

const TPC_LOCATION_URL =
  "https://www.google.com/maps/search/?api=1&query=Floor+2+Landmark+Building+13+Umgazi+Road+Menlo+Park+Pretoria+0081";

const CONTACT_CHANNELS = [
  {
    id: "email",
    icon: contactAndUsEmailIcon,
    eyebrow: "Shared enquiries",
    value: "info@tpc.training",
    description:
      "The best starting point for AndUs product, platform and custom-system enquiries.",
    href: "mailto:info@tpc.training?subject=AndUs%20enquiry",
    actionLabel: "Send an email",
    tone: "blue",
  },
  {
    id: "reception",
    icon: contactAndUsPhoneIcon,
    eyebrow: "TPC Head Office reception",
    value: "012 051 3483",
    description:
      "Contact the shared TPC reception team and ask to be connected with AndUs.",
    href: "tel:+27120513483",
    actionLabel: "Call reception",
    tone: "pink",
  },
  {
    id: "support",
    icon: contactAndUsPhoneIcon,
    eyebrow: "TPC support line",
    value: "+27 82 732 3216",
    description:
      "Use the shared support channel when you need help with an existing system.",
    href: "tel:+27827323216",
    actionLabel: "Call support",
    tone: "gold",
  },
];

function ContactAndUsArrowIcon() {
  return (
    <svg
      className="contact-andus-section__arrow"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3.75 10H16.25M11.25 5L16.25 10L11.25 15"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ContactChannelCard({ channel }) {
  return (
    <article
      className={`contact-andus-section__channel-card contact-andus-section__channel-card--${channel.tone}`}
    >
      <span className="contact-andus-section__channel-icon">
        <img
          src={channel.icon}
          alt=""
          aria-hidden="true"
        />
      </span>

      <p className="contact-andus-section__channel-eyebrow">
        {channel.eyebrow}
      </p>

      <h2 className="contact-andus-section__channel-value">
        {channel.value}
      </h2>

      <p className="contact-andus-section__channel-description">
        {channel.description}
      </p>

      <a
        className="contact-andus-section__channel-action"
        href={channel.href}
      >
        <span>{channel.actionLabel}</span>
        <ContactAndUsArrowIcon />
      </a>
    </article>
  );
}

export default function ContactAndUsSection() {
  return (
    <section
      id="contact-andus-content"
      className="contact-andus-section"
      aria-labelledby="contact-andus-section-title"
    >
      <div
        className="contact-andus-section__background"
        aria-hidden="true"
      >
        <span className="contact-andus-section__glow contact-andus-section__glow--blue" />
        <span className="contact-andus-section__glow contact-andus-section__glow--pink" />
        <span className="contact-andus-section__grid" />
      </div>

      <div className="contact-andus-section__inner">
        <header className="contact-andus-section__hero">
          <div className="contact-andus-section__hero-copy">
            <div className="contact-andus-section__eyebrow">
              <span
                className="contact-andus-section__eyebrow-dot"
                aria-hidden="true"
              />

              <span>Contact &amp;Us</span>
            </div>

            <h1
              id="contact-andus-section-title"
              className="contact-andus-section__title"
            >
              Let’s build something useful.
            </h1>

            <p className="contact-andus-section__intro">
              Talk to us about Unity Learning, TalentLenz, FleetCore,
              Work Passport or a custom intelligent system designed
              around your operation.
            </p>
          </div>

          <aside className="contact-andus-section__routing-note">
            <p className="contact-andus-section__routing-label">
              Shared TPC infrastructure
            </p>

            <p className="contact-andus-section__routing-copy">
              AndUs enquiries are received through the Performance
              Centre Group’s established reception, support and
              public-enquiry channels.
            </p>

            <a
              className="contact-andus-section__routing-action"
              href="mailto:info@tpc.training?subject=AndUs%20enquiry"
            >
              <img
                src={contactAndUsEmailIcon}
                alt=""
                aria-hidden="true"
              />

              <span>Start with an email</span>
              <ContactAndUsArrowIcon />
            </a>
          </aside>
        </header>

        <div
          className="contact-andus-section__channel-grid"
          aria-label="AndUs contact channels"
        >
          {CONTACT_CHANNELS.map((channel) => (
            <ContactChannelCard
              key={channel.id}
              channel={channel}
            />
          ))}
        </div>

        <div className="contact-andus-section__details-grid">
          <article className="contact-andus-section__office-card">
            <div className="contact-andus-section__office-icon">
              <img
                src={contactAndUsLocationIcon}
                alt=""
                aria-hidden="true"
              />
            </div>

            <div className="contact-andus-section__office-copy">
              <p className="contact-andus-section__detail-eyebrow">
                TPC Head Office · Pretoria
              </p>

              <h2>Visit our shared head office.</h2>

              <address>
                Floor 2, Landmark Building
                <br />
                13 Umgazi Road, Menlo Park
                <br />
                Pretoria, 0081
              </address>

              <a
                className="contact-andus-section__detail-action"
                href={TPC_LOCATION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Open in Google Maps</span>
                <ContactAndUsArrowIcon />
              </a>
            </div>
          </article>

          <article className="contact-andus-section__group-card">
            <div className="contact-andus-section__group-header">
              <div>
                <p className="contact-andus-section__detail-eyebrow contact-andus-section__detail-eyebrow--gold">
                  Part of the Performance Centre Group
                </p>

                <h2>
                  Applied intelligence, backed by shared capability.
                </h2>
              </div>

              <img
                className="contact-andus-section__tpc-logo"
                src={contactAndUsTpcLogo}
                alt="TPC Performance Centre"
              />
            </div>

            <p className="contact-andus-section__group-copy">
              AndUs is the TPC Group’s applied-intelligence technology
              subsidiary and operates from the Group’s Pretoria head
              office.
            </p>

            <div className="contact-andus-section__group-actions">
              <a
                className="contact-andus-section__detail-action"
                href={TPC_WEBSITE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Visit the TPC website</span>
                <ContactAndUsArrowIcon />
              </a>

              <a
                className="contact-andus-section__linkedin-action"
                href={TPC_LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={contactAndUsLinkedinIcon}
                  alt=""
                  aria-hidden="true"
                />

                <span>LinkedIn</span>
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}